import {Injectable, Optional} from '@angular/core';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';

import {Observable, of} from 'rxjs';

import {LoaderService} from '../loader/loader.service';
import {LoginSession} from './LoginSession';
import {Configurations} from '../../../Configuration';
import {tap} from 'rxjs/operators';

export enum MessageServiceState {
    Running,
    Success,
    Failed,
    Error
}

@Injectable()
export class MessageService {

    config: Configurations;

    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        @Optional() config?: Configurations
    ) {
        this.config = config;
    }

    protected isJson(item?: any): boolean {
        if (item && typeof item === 'object') {
            return true;
        }

        try {
            JSON.parse(item);
        } catch (e) {
            return false;
        }

        return true;
    }

    protected getData(method?: string, params?: any, contentType?: string): any {
        if (method && (method === 'get' || method === 'query')) {
            if (params) {
                if (contentType === 'application/json') {
                    if (this.isJson(params)) {
                        return escape(JSON.stringify(params));
                    } else {
                        return params;
                    }
                } else {
                    return params;
                }
            } else {
                return null;
            }
        } else {
            return params;
            // return ((this.isJson(params) && (!contentType || contentType === 'application/json')) ? params : params);
        }
    }

    getDefaultHeaders(contentType?: string): HttpHeaders {
        if (localStorage.getItem(LoginSession.SessionKey)) {
            try {
                const session = JSON.parse(localStorage.getItem(LoginSession.SessionKey));

                if (this.config.testUserId && !this.config.getProduction()) {
                    return Configurations.buildTestCommonHeader(this.config, contentType);
                } else {
                    if (localStorage.getItem('locale')) {
                        return new HttpHeaders({
                            'Content-Type': (contentType ? contentType : 'application/json'),
                            'LoginId': session.loginId,
                            'AccessKey': session.accessKey,
                            'SecretKey': session.secretKey,
                            'Accept-Language': localStorage.getItem('locale')
                        });
                    } else {
                        return new HttpHeaders({
                            'Content-Type': (contentType ? contentType : 'application/json'),
                            'LoginId': session.loginId,
                            'AccessKey': session.accessKey,
                            'SecretKey': session.secretKey
                        });
                    }
                }
            } catch (error) {
                return Configurations.buildTestCommonHeader(this.config, contentType);
            }
        } else if (!this.config.getProduction()) {
            return Configurations.buildTestCommonHeader(this.config, contentType);
        }
    }

    protected getHttpOptions(requestData: any, contentType?: string, responseType?: string, ignoreLoader?: boolean, isProgress?: boolean): any {
        this.onBegin(ignoreLoader);

        if (!responseType) {
            return {
                headers: this.getDefaultHeaders(contentType),
                params: requestData,
            };
        } else {
            if (isProgress) {
                return {
                    headers: this.getDefaultHeaders(contentType),
                    params: requestData,
                    observe: 'events',
                    reportProgress: true,
                    responseType: responseType
                };
            } else {
                return {
                    headers: this.getDefaultHeaders(contentType),
                    params: requestData,
                    responseType: responseType
                };
            }

        }
    }

    protected onBegin(ignoreLoader?: boolean) {
        if (!ignoreLoader) {
            this.loaderService.show();
        }
    }

    protected onEnd(ignoreLoader?: boolean) {
        if (!ignoreLoader) {
            this.loaderService.hide();
        }
    }

    text(requestUrl: string, params?: any, contentType?: string, responseType?: string, handleNoError?: boolean): Observable<any> {
        return this.http.get(requestUrl, this.getHttpOptions(this.getData('get', params, contentType), contentType, responseType))
            .pipe(tap(_ => this.log('fetched query data'), this.handleError<any>('MessageService::query', {}, handleNoError), () => this.onEnd()));
    }

    query(requestUrl: string, params?: any, contentType?: string, responseType?: string, handleNoError?: boolean): Observable<any> {
        return this.http.get<any>(requestUrl, this.getHttpOptions(this.getData('get', params, contentType), contentType, responseType))
            .pipe(tap(_ => this.log('fetched query data'), this.handleError<any>('MessageService::query', {}, handleNoError), () => this.onEnd()));
    }

    file(requestUrl: string, params?: any, contentType?: string, responseType?: string, handleNoError?: boolean, isProgress?: boolean): Observable<any> {
        if (!requestUrl.includes('?')) {
            requestUrl += '?'
        }
        requestUrl += '&file_timestamp=' + new Date().getTime();

        return this.http.get(requestUrl, this.getHttpOptions(this.getData('get', params, contentType), contentType, responseType, handleNoError, isProgress))
            .pipe(tap(_ => this.log('fetched query data'), this.handleError<any>('MessageService::file', {}, handleNoError), () => this.onEnd()));
    }

    put(requestUrl: string, body?: any, params?: any, contentType?: string, handleNoError?: boolean): Observable<any> {
        return this.http.put<any>(requestUrl, body, this.getHttpOptions(this.getData('put', params, contentType)))
            .pipe(tap(_ => this.log('fetched put data'), this.handleError<any>('MessageService::put', {}, handleNoError), () => this.onEnd()));
    }

    post(requestUrl: string, body?: any, params?: any, contentType?: string, handleNoError?: boolean): Observable<any> {
        return this.http.post<any>(requestUrl, body, this.getHttpOptions(this.getData('post', params, contentType)))
            .pipe(tap(_ => this.log('fetched post data'), this.handleError<any>('MessageService::post', {}, handleNoError), () => this.onEnd()));
    }

    postResponse(requestUrl: string, body?: any, params?: any, contentType?: string, handleNoError?: boolean): Observable<HttpResponse<any>> {
        return this.http.post<any>(requestUrl, body, {
            headers: this.getDefaultHeaders(contentType),
            params: this.getData('post', params, contentType),
            observe: 'response'
        })
            .pipe(tap(_ => this.log('fetched post data'), this.handleError<any>('MessageService::postResponse', {}, handleNoError), () => this.onEnd()));
    }

    postResponseBlob(requestUrl: string, body?: any, params?: any, contentType?: string, handleNoError?: boolean): Observable<HttpResponse<any>> {
        return this.http.post(requestUrl, body, {
            headers: this.getDefaultHeaders(contentType),
            params: this.getData('post', params, contentType),
            observe: 'response',
            responseType: 'blob'
        })
            .pipe(tap(_ => this.log('fetched post data'), this.handleError<any>('MessageService::postResponseBlob', {}, handleNoError), () => this.onEnd()));
    }

    patch(requestUrl: string, body?: any, params?: any, contentType?: string, ignoreLoader?: boolean, handleNoError?: boolean): Observable<any> {
        return this.http.patch<any>(requestUrl, body, this.getHttpOptions(this.getData('patch', params, contentType), null, null, ignoreLoader))
            .pipe(tap(_ => this.log('fetched patch data'), this.handleError<any>('MessageService::patch', {}, handleNoError), () => this.onEnd(ignoreLoader)));
    }

    delete(requestUrl: string, params?: any, contentType?: string, handleNoError?: boolean): Observable<any> {
        return this.http.delete<any>(requestUrl, this.getHttpOptions(this.getData('delete', params, contentType)))
            .pipe(tap(_ => this.log('fetched delete data'), this.handleError<any>('MessageService::delete', {}, handleNoError), () => this.onEnd()));
    }

    // delete(requestUrl: string, params?: any, contentType?: string): Observable<any> {
    // 	return this.http.delete<any>(requestUrl, this.getHttpOptions(this.getData('delete', params, contentType)))
    // 		.pipe(tap(_ => this.log('fetched delete data'), null, () => this.onEnd()), catchError(this.handleError<any>('MessageService::delete', {})));
    // }

    deleteWithBody(requestUrl: string, body?: any, params?: any, contentType?: string): Observable<HttpResponse<any>> {
        return this.http.request<any>('DELETE', requestUrl, {
            body: body,
            headers: this.getDefaultHeaders(contentType),
            params: this.getData('delete', params, contentType),
            observe: 'response',
            responseType: 'json'
        }).pipe(tap(_ => this.log('delete result data'), this.handleError<any>('MessageService::deleteWithBody', {}), () => this.onEnd()));
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result - optional value to return as the observable result
     */
    private handleError<T>(operation = 'operation', result?: T, handleNoError?: boolean) {
        return (error: any): Observable<T> => {
            // TODO: send the error to remote logging infrastructure
            console.error(error); // log to console instead

            if (error.status !== 417 && error.status !== 403) {
            }
            this.onEnd();
            this.log(`${operation} failed: ${error.message}`);

            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }

    /** Log a HeroService message with the NotificationService */
    private log(message: string) {
        //console.log(message);
    }
}
